import { BookProcessingStatus } from '@masters-club/types';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react';

interface BookProcessingStatusBadgeProps {
  status: BookProcessingStatus;
  error?: string | null;
}

const STATUS_META: Record<
  BookProcessingStatus,
  { label: string; className: string; icon: React.ReactNode }
> = {
  [BookProcessingStatus.PENDING]: {
    label: 'Ожидает',
    className: 'bg-yellow-500/10 text-yellow-600',
    icon: <Clock className="h-3 w-3" />,
  },
  [BookProcessingStatus.PROCESSING]: {
    label: 'Обработка',
    className: 'bg-blue-500/10 text-blue-600',
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
  },
  [BookProcessingStatus.READY]: {
    label: 'Готово',
    className: 'bg-green-500/10 text-green-600',
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  [BookProcessingStatus.ERROR]: {
    label: 'Ошибка',
    className: 'bg-red-500/10 text-red-600',
    icon: <AlertCircle className="h-3 w-3" />,
  },
};

export function BookProcessingStatusBadge({ status, error }: BookProcessingStatusBadgeProps) {
  const meta = STATUS_META[status];
  const badge = (
    <Badge variant="secondary" className={meta.className}>
      <span className="mr-1 inline-flex items-center">{meta.icon}</span>
      {meta.label}
    </Badge>
  );

  if (status === BookProcessingStatus.ERROR && error) {
    return (
      <div className="space-y-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help">{badge}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs whitespace-pre-wrap break-words">
            {error}
          </TooltipContent>
        </Tooltip>
        <p className="text-xs text-destructive line-clamp-2">{error}</p>
      </div>
    );
  }

  return <div className="space-y-1">{badge}</div>;
}
